import { getCommonAnalyticsProps } from '../helpers/analytics';
import { getCommonProps } from '../helpers/commonHelpers';
import { SIZE_CHART_COL_TYPES } from '../../../enum/componentAttributes';

const isMeasurement = item =>
  item.type === SIZE_CHART_COL_TYPES.MEASUREMENT && item.system !== 'UNIVERSAL';

const getSystemHash = measurement => {
  const systems = measurement?.map?.(header => header?.system) ?? [];
  const uniqueSystems = [...new Set(systems)];
  return uniqueSystems?.sort()?.join('/') ?? '';
};

export const getToggleOption = (localizationStrings, measurement = {}) => ({
  id: measurement?.key,
  label:
    localizationStrings?.find?.(str => str.key === measurement?.label)?.value ??
    measurement?.label,
  value: measurement?.system,
});

const getHeader = ({ key, system, type }, localizationStrings) => ({
  key,
  ...(isMeasurement({ system, type }) && { system }),
  value: localizationStrings?.find?.(str => str.key === key)?.value ?? key,
});

export const prepareCardData = (data, { nodes }) => ({
  ...getCommonProps(data),
  description: data?.properties?.subtitle,
  sizeCharts:
    nodes?.[data?.id]?.properties?.custom?.size_charts?.map(table => {
      const sizes = (table?.sizes ?? []).sort((a, b) => a.row - b.row);
      const localizationStrings =
        nodes?.[data?.id]?.properties?.custom?.localizationStrings;
      const measurement =
        table?.headers?.filter?.(isMeasurement)?.map?.(m => ({
          ...m,
          label:
            table?.[
              `toggleKey${m?.system
                ?.toLowerCase?.()
                ?.replace?.(/(^|\s)\S/g, l => l?.toUpperCase?.())}`
            ],
        })) ?? [];

      return {
        data: (table?.headers ?? []).map(header => [
          getHeader(header, localizationStrings),
          ...sizes.map((size, index) => {
            let cellData = {};

            if (header?.type === SIZE_CHART_COL_TYPES.RETAIL_SIZE) {
              cellData = { value: size?.size };
            }

            if (header?.type === SIZE_CHART_COL_TYPES.CONVERSION) {
              const value = size?.conversions?.find?.(
                c => c?.header === header?.key,
              )?.convertedSize;
              cellData = { value };
            }

            if (header?.type === SIZE_CHART_COL_TYPES.MEASUREMENT) {
              const value = size?.measurements?.find?.(
                m => m?.unit === header?.system && header?.key === m?.header,
              )?.measurement;
              cellData = {
                ...(header?.system !== 'UNIVERSAL' && {
                  system: header?.system,
                }),
                value,
              };
            }
            return {
              key: `${header?.key}_${index}`,
              ...cellData,
            };
          }),
        ]),
        id: table?.id,
        measurements:
          measurement?.length > 0
            ? {
                systemToggle: getSystemHash(measurement),
                values: [
                  getToggleOption(localizationStrings, measurement[0]),
                  getToggleOption(
                    localizationStrings,
                    measurement[measurement.length - 1],
                  ),
                ],
              }
            : null,
      };
    }) ?? [],
  title: data?.properties?.title,
});

export { getUrlIngredients, getBuyingToolsIngredients } from './common';

export const prepareTranslations = (translationStrings, cardData) => {
  let a11ySwitchDescriptions = {};
  try {
    a11ySwitchDescriptions = [
      ...new Set(
        cardData.sizeCharts
          .map(sizeChart =>
            sizeChart?.measurements?.values?.map(m =>
              m?.label?.replace('.', ''),
            ),
          )
          .filter(units => units && units.length > 0)
          .map(
            units => `toggle_${units[0]}_${units[units.length - 1]}_a11y_desc`,
          ),
      ),
    ].reduce(
      (acc, curVal) => ({
        ...acc,
        [curVal]: translationStrings[curVal],
      }),
      {},
    );
  } catch (e) {
    console.error(e);
  }

  return {
    ...a11ySwitchDescriptions, // ie: { toggle_in_cm_a11y_desc: 'Toggle between inches and centimeters' }
    defaultTitle: translationStrings?.size_chart_title,
  };
};

export const mergeProperties = ({ cardData, translations }) => ({
  ...cardData,
  title: cardData.title || translations.defaultTitle,
  translations,
});

export const getAnalyticsPlacements = getCommonAnalyticsProps;
